import { Anchor, Button, Table, Tabs } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { stevilo } from '../oblika/oblika';
import { GlavaZaslona, IskalnoPolje, Seznam, Stolpec, ZaslonSeznama } from '../seznam/Seznam';
import { useSeznam } from '../seznam/useSeznam';
import { imeOsebe, naslov, VRSTE, vrsta as poKljucu } from './vrste';
import { usePravica } from '../seja/pravice';

/** Ključi razvrščanja (OsebeKontroler.pregled). */
type Razvrsti = 'ime' | 'naslov' | 'davcna' | 'idZaDdv' | 'emso' | 'oib';

const pregled = (vrsta: string, iskanje: string, zaApi: object, signal?: AbortSignal) =>
  podatki(
    api.GET('/api/osebe/{vrsta}/pregled', {
      signal,
      params: { path: { vrsta }, query: { iskanje: iskanje || undefined, ...zaApi } },
    }),
  );

/**
 * Pregled oseb – kot PregledOseb v Swingu: zavihki Kupci / Prodajalci / Uporabniki vozil s številom zadetkov za
 * trenutno iskanje (po vseh poljih, tudi podrobnostih), razvrščanje in strani na strežniku, »Nov …«; klik na vrstico
 * odpre podrobnosti.
 */
export function PregledOseb() {
  const ureja = usePravica('STRANKE', 'UREJANJE');
  const seznam = useSeznam<Razvrsti>();
  const navigate = useNavigate();
  const izbrana = poKljucu(seznam.parameter('vrsta'));
  const poizvedba = useQuery({
    queryKey: ['osebe', izbrana.kljuc, seznam.iskanje, seznam.zaApi],
    queryFn: ({ signal }) => pregled(izbrana.kljuc, seznam.iskanje, seznam.zaApi, signal),
    placeholderData: keepPreviousData,
  });
  // število zadetkov na ostalih zavihkih (ena vrstica na vrsto)
  const stevila = useQueries({
    queries: VRSTE.map((v) => ({
      queryKey: ['osebe-stevilo', v.kljuc, seznam.iskanje],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        pregled(v.kljuc, seznam.iskanje, { stran: 0, velikost: 1 }, signal),
      enabled: v.kljuc !== izbrana.kljuc,
    })),
  });
  const vrstice = poizvedba.data?.vsebina ?? [];
  const steviloZa = (i: number) =>
    VRSTE[i]!.kljuc === izbrana.kljuc ? poizvedba.data?.skupaj : stevila[i]?.data?.skupaj;

  return (
    <ZaslonSeznama>
      <GlavaZaslona
        naslov="Osebe"
        opis="Kupci, prodajalci in uporabniki vozil."
        dejanja={
          ureja && (
            <Button component={Link} to={`/osebe/${izbrana.kljuc}/nova`} leftSection={<IconPlus size={18} />}>
              {izbrana.nova}
            </Button>
          )
        }
      />
      <Seznam
        seznam={seznam}
        filtri={
          <IskalnoPolje seznam={seznam} namig="Iskanje po imenu, priimku, naslovu, davčni, EMŠO, telefonu, e-pošti …" />
        }
        nadTabelo={
          <Tabs
            value={izbrana.kljuc}
            onChange={(v) => seznam.nastavi({ vrsta: v === 'kupci' ? null : v, razvrsti: null, smer: null })}
            mb="sm"
          >
            <Tabs.List style={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
              {VRSTE.map((v, i) => (
                <Tabs.Tab key={v.kljuc} value={v.kljuc} style={{ whiteSpace: 'nowrap' }}>
                  {v.naziv}
                  {steviloZa(i) !== undefined && ` (${stevilo(steviloZa(i)!)})`}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        }
        glava={
          <>
            <Stolpec seznam={seznam} kljuc="ime" naziv="Ime in priimek" />
            <Stolpec seznam={seznam} kljuc="naslov" naziv="Naslov" />
            <Stolpec seznam={seznam} kljuc="davcna" naziv="Davčna" />
            <Stolpec seznam={seznam} kljuc="idZaDdv" naziv="ID za DDV" />
            <Stolpec seznam={seznam} kljuc="emso" naziv="EMŠO / Mat. št." />
            <Stolpec seznam={seznam} kljuc="oib" naziv="OIB" />
          </>
        }
        vrstice={vrstice.map((o, i) => {
          const pot = `/osebe/${izbrana.kljuc}/${o.id}`;
          return (
            <Table.Tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => void navigate(pot)}>
              <Table.Td c="dimmed" className="brez-preloma">
                {seznam.zamik + i + 1}
              </Table.Td>
              <Table.Td>
                <Anchor component={Link} to={pot} onClick={(e) => e.stopPropagation()} fw={600}>
                  {imeOsebe(o) || '–'}
                </Anchor>
              </Table.Td>
              <Table.Td>{naslov(o)}</Table.Td>
              <Table.Td className="brez-preloma">{o.davcna}</Table.Td>
              <Table.Td>{o.idZaDdv}</Table.Td>
              <Table.Td>{o.emso || o.maticnaSt}</Table.Td>
              <Table.Td>{o.oib}</Table.Td>
            </Table.Tr>
          );
        })}
        stVrstic={vrstice.length}
        stStolpcev={6}
        skupaj={poizvedba.data?.skupaj ?? 0}
        nalaganje={poizvedba.isPending}
        osvezevanje={poizvedba.isFetching}
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        prazno="Ni oseb za izbrane pogoje."
        napakaNaslov="Oseb ni bilo mogoče naložiti"
      />
    </ZaslonSeznama>
  );
}
